import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { A11y, Autoplay, EffectFade, Mousewheel, Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Box, IconButton } from '@chakra-ui/react';
import { ChevronRightIcon } from '@udacity/chakra-uds-icons';

import { trackEvent } from '~/features/analytics/track-event';
import { ChevronLeftIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { useDelayedSwiperAutoplay } from '~/features/promotions/hooks/use-delayed-swiper-autoplay';
import { useClientRender } from '~/hooks/use-client-render';
import { useI18n } from '~/hooks/use-i18n';

import 'swiper/css';
import 'swiper/css/mousewheel';
import 'swiper/css/autoplay';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

export function CardCarousel<
  T extends {
    key: string;
  },
>({
  autoplayStartDelayMs = 0,
  autoplayStartOnViewport = false,
  items,
  renderCard,
  isReady = true,
}: {
  /** Ms to wait after the carousel is ready (and in viewport, if required) before autoplay starts. */
  autoplayStartDelayMs?: number;
  /** When true, the delay countdown begins only once the carousel intersects the viewport. */
  autoplayStartOnViewport?: boolean;
  items: T[];
  renderCard: ({ item, index }: { item: T; index: number }) => ReactNode;
  /**
   * Extra readiness gate (e.g. `usePromotion().isFetched`). Holds the carousel at `opacity: 0`
   * until true so a conditionally prepended first slide cannot cause CLS once it resolves.
   */
  isReady?: boolean;
}) {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const bannerEventRef = useRef<Record<number, boolean>>({});
  const isClientReady = useClientRender();
  // Mirror the catalog hero: hold hidden until the promo settles, then remount so the carousel
  // starts on slide 0 (the prepended flash slide) instead of wherever hidden autoplay drifted.
  const isCarouselVisible = isClientReady && isReady;
  const { onDestroy: onSwiperDestroy, onSwiper } = useDelayedSwiperAutoplay({
    isEnabled: isCarouselVisible,
    isInViewport: isIntersecting,
    requiresViewport: autoplayStartOnViewport,
    startDelayMs: autoplayStartDelayMs,
  });
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const { i18n, isRtl } = useI18n();
  const cards = useMemo(() => {
    return items.map((item, index) => {
      return <SwiperSlide key={item.key}>{renderCard({ item, index })}</SwiperSlide>;
    });
  }, [items, renderCard]);

  const handleBannerLoad = useCallback(
    (realIndex: number) => {
      // Don't record views while the carousel is still held hidden (opacity: 0) waiting on the
      // promo gate. Autoplay would otherwise fire `onRealIndexChange` for the pre-flash slide and
      // poison `bannerEventRef`, suppressing the real flash-slide view once the promo prepends a
      // slide and the swiper remounts.
      if (!isCarouselVisible) {
        return;
      }
      const hasFiredEvent = bannerEventRef.current[realIndex];
      if (isIntersecting && !hasFiredEvent) {
        bannerEventRef.current[realIndex] = true;
        trackEvent({
          name: 'Banner Viewed',
          type: 'view',
          label: 'hero_banner_viewed',
          value: items[realIndex].key,
        });
      }
    },
    [items, isIntersecting, isCarouselVisible]
  );

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(([entry]) => {
      setIsIntersecting(entry.isIntersecting);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Emit the initial `Banner Viewed` for slide 0 once the carousel is visible (promo settled) and in
  // view. Swiper doesn't fire `onRealIndexChange` for the initial slide, so without this the prepended
  // flash slide's view would never be tracked. `handleBannerLoad` re-runs the effect when intersection
  // changes (covering the below-the-fold case) and dedupes via `bannerEventRef`. Mirrors the catalog
  // hero's ValuePropsCarousel.
  useEffect(() => {
    if (!isCarouselVisible) {
      return;
    }
    handleBannerLoad(0);
  }, [handleBannerLoad, isCarouselVisible]);

  return (
    <Box ref={containerRef} position='relative'>
      <Box
        isolation='isolate'
        position='relative'
        // Reserve the carousel height from first paint so the section below doesn't shift. The
        // `.swiper` stays collapsed (height:0) while hidden — avoiding the pre-init width:1px layout
        // flash and the wrong-slide height during the promo-pending phase — then expands once into
        // this reserved space when the promo settles. Values match the rendered first-slide height
        // (~431px mobile / ~394px desktop). Mirrors the catalog hero's ValuePropsCarousel.
        minHeight={{
          base: '432px',
          md: '394px',
        }}
        sx={{
          '.swiper': {
            opacity: isCarouselVisible ? 1 : 0,
            height: isCarouselVisible ? undefined : '0px',
            transition: 'opacity 0.2s ease',
            paddingBottom: '48px',
          },
          '.swiper-slide': {
            height: 'auto',
            display: 'flex',
          },
          '.swiper-slide > *': {
            width: '100%',
          },
          '.swiper-pagination': {
            height: '30px',
          },
          '.swiper-pagination-bullet': {
            backgroundColor: 'blue.300',
            width: '16px',
            borderRadius: '8px',
            height: '4px',
            opacity: 0.33,
            transition: 'all 0.3s ease',
          },
          '.swiper-pagination-bullet-active': {
            backgroundColor: 'blue.300',
            opacity: 1,
            width: '56px',
            borderRadius: '8px',
            height: '4px',
          },
          // Note: this is a weird hack to make the swiper slide widths responsive.
          '.swiper-wrapper': {
            width: '1px',
          },
        }}
      >
        <Swiper
          key={isCarouselVisible ? 'ready' : 'pending'}
          direction='horizontal'
          effect='fade'
          longSwipes={false}
          modules={[A11y, Mousewheel, EffectFade, Autoplay, Navigation, Pagination]}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
            stopOnLastSlide: false,
            waitForTransition: true,
          }}
          fadeEffect={{
            crossFade: true,
          }}
          mousewheel={{
            forceToAxis: true,
            releaseOnEdges: true,
            thresholdDelta: 10,
          }}
          navigation={{
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }}
          pagination={{
            clickable: true,
          }}
          loop
          onDestroy={onSwiperDestroy}
          onSwiper={onSwiper}
          onBeforeInit={(swiper) => {
            swiper.changeLanguageDirection(i18n.dir());
          }}
          onRealIndexChange={(swiper) => {
            handleBannerLoad(swiper.realIndex);
          }}
        >
          {isClientReady ? cards : cards.slice(0, 1)}
        </Swiper>
      </Box>
      <IconButton
        ref={prevRef}
        aria-label='Previous'
        color='grey.500'
        icon={isRtl ? <ChevronRightIcon height='36px' width='36px' /> : <ChevronLeftIcon height='36px' width='36px' />}
        isDisabled={!isClientReady}
        left='-64px'
        opacity={0.5}
        position='absolute'
        size='md'
        top='calc(50% - 48px)'
        variant='ghost'
        display={{
          base: 'none',
          xl: 'inline-flex',
        }}
      />
      <IconButton
        ref={nextRef}
        aria-label='Next'
        color='grey.500'
        icon={isRtl ? <ChevronLeftIcon height='36px' width='36px' /> : <ChevronRightIcon height='36px' width='36px' />}
        isDisabled={!isClientReady}
        opacity={0.5}
        position='absolute'
        right='-64px'
        size='md'
        top='calc(50% - 48px)'
        variant='ghost'
        display={{
          base: 'none',
          xl: 'inline-flex',
        }}
      />
    </Box>
  );
}
